import React from 'react';
import { Card } from '../components/ui/card';
import { Shield } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <main className="pt-24 bg-[#FDFDFB]">
      <SEO 
        title="Notice of Privacy Practices (HIPAA)"
        description="HIPAA Notice of Privacy Practices for Harmony Behavioral & Psychiatric Health."
      />

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 bg-white border-[#d4cec5]">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="text-[#7a8a72]" size={40} />
                <h1 className="text-4xl font-bold text-[#7a8a72]">Notice of Privacy Practices (HIPAA)</h1>
              </div>

              <h2 className="text-2xl font-semibold text-[#7a8a72] mb-2">Harmony Behavioral & Psychiatric Health</h2>
              <p className="text-[#3a3a3a] mb-6"><strong>Effective Date:</strong> January 23, 2026</p>

              <p className="text-[#3a3a3a] mb-6 leading-relaxed font-semibold">
                This Notice describes how medical information about you may be used and disclosed and how you can access this information. Please review it carefully.
              </p>

              <div className="space-y-8 text-[#3a3a3a]">
                <div>
                  <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">OUR LEGAL DUTIES</h3>
                  <p className="mb-3">Harmony Behavioral & Psychiatric Health is required by law to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Maintain the privacy of your protected health information (PHI)</li>
                    <li>Provide you with this Notice of Privacy Practices</li>
                    <li>Follow the terms of this Notice currently in effect</li>
                  </ul>
                  <p className="mt-3 leading-relaxed">
                    We reserve the right to change this Notice and make the revised Notice effective for all PHI we maintain. Updated notices will be posted on our website and made available upon request.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">HOW WE MAY USE AND DISCLOSE YOUR PROTECTED HEALTH INFORMATION</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Treatment</h4>
                      <p className="leading-relaxed">
                        To provide, coordinate, or manage your care with other healthcare professionals, pharmacies, laboratories, and clinical services.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Payment</h4>
                      <p className="leading-relaxed">
                        To bill and collect payment from insurance companies, third-party payers, or directly from you.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Health Care Operations</h4>
                      <p className="leading-relaxed">
                        For quality improvement, compliance, training, administrative operations, care coordination, and practice management activities.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Business Associates</h4>
                      <p className="leading-relaxed">
                        We may share PHI with contracted service providers (billing services, EHR platforms, IT services, and compliance vendors) who are legally required to protect your information.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Appointment Reminders</h4>
                      <p className="leading-relaxed">
                        We may contact you via phone, text, email, or secure patient portal messaging regarding appointments and care coordination.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Legal and Public Health</h4>
                      <p className="leading-relaxed">
                        We may disclose PHI when required by law, for public health reporting, court orders, law enforcement requests, workers' compensation claims, and regulatory compliance.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-[#7a8a72] mb-2">Authorization</h4>
                      <p className="leading-relaxed">
                        We will not disclose PHI for purposes not listed in this Notice without your written authorization. You may revoke authorization in writing at any time.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">YOUR RIGHTS</h3>
                  <p className="mb-3">You have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access your medical records</li>
                    <li>Request amendments to your records</li>
                    <li>Receive an accounting of disclosures</li>
                    <li>Request restrictions on certain uses/disclosures</li>
                    <li>Request confidential communications</li>
                    <li>Obtain a paper copy of this Notice</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">COMPLAINTS</h3>
                  <p className="mb-3 leading-relaxed">
                    If you believe your privacy rights have been violated, you may file a complaint with:
                  </p>
                  <Card className="p-4 bg-[#f7f4f0] border-[#d4cec5]">
                    <p className="font-semibold">Harmony Behavioral & Psychiatric Health</p>
                    <p>1123 Clairmont Rd</p>
                    <p>Decatur, GA 30030</p>
                    <p>Phone: 404-738-7542</p>
                    <p>Fax: 678-737-1486</p>
                    <p>Email: info@harmonyhealthwell.com</p>
                  </Card>
                  <p className="mt-3 leading-relaxed">
                    You may also file a complaint with the U.S. Department of Health and Human Services. Contact information will be provided upon request.
                  </p>
                  <p className="mt-3 font-semibold">
                    We will not retaliate against you for filing a complaint.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#7a8a72] mb-3">PRIVACY OFFICER / CONTACT PERSON</h3>
                  <Card className="p-4 bg-[#f7f4f0] border-[#d4cec5]">
                    <p className="font-semibold">Harmony Behavioral & Psychiatric Health</p>
                    <p>1123 Clairmont Rd</p>
                    <p>Decatur, GA 30030</p>
                    <p>Phone: 404-738-7542</p>
                    <p>Fax: 678-737-1486</p>
                    <p>Email: info@harmonyhealthwell.com</p>
                  </Card>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;